
import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ArrowLeft, Share2, FlaskConical } from 'lucide-react';
import Layout from '../components/Layout';
import { Btn, Spinner, Alert, Section, PageHeader, Badge } from '../components/UI';
import { API_URL, API_KEY } from '../config';

function isNormal(result, refRange) {
  if (!result) return true;
  const s = result.toString().toLowerCase().trim();
  if (['normal', 'negative', 'trace'].some(k => s.includes(k))) return true;
  if (['moderate', 'small', 'large', 'high', 'low', 'positive', 'marked'].some(k => s.includes(k))) return false;
  const val = parseFloat(result);
  if (!isNaN(val) && refRange) {
    const m = refRange.match(/([\d.]+)\s*[-:]\s*([\d.]+)/);
    if (m) {
      const lo = parseFloat(m[1]), hi = parseFloat(m[2]);
      if (!isNaN(lo) && !isNaN(hi)) return val >= lo && val <= hi;
    }
    return false;
  }
  return true;
}

export default function HistoryPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { id, patient_id, timestamp } = location.state || {};
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchResult = async () => {
      const fetchId = patient_id || id;
      const userEmail = localStorage.getItem('userEmail');

      if (!fetchId) {
        setError('No record ID provided.');
        setLoading(false);
        return;
      }

      try {
        const res = await fetch(`${API_URL}/api/results/${fetchId}`, {
          headers: { 'Cytometrix-API-KEY': API_KEY }
        });

        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const resp = await res.json();

        let selected = null;

        if (resp?.visits && Array.isArray(resp.visits)) {
          // Filter visits to only those belonging to the logged-in user
          const myVisits = userEmail
            ? resp.visits.filter(v => v.email === userEmail)
            : resp.visits;

          if (myVisits.length === 0) {
            setError('You do not have access to this record.');
            setLoading(false);
            return;
          }

          if (timestamp) selected = myVisits.find(v => v.timestamp === timestamp);
          else if (id) selected = myVisits.find(v => v.id === id);
          if (!selected) selected = myVisits[0];

        } else if (resp?.timestamp) {
          // Check ownership for single record response
          if (userEmail && resp.email !== userEmail) {
            setError('You do not have access to this record.');
            setLoading(false);
            return;
          }
          selected = resp;
        }

        setData(selected);
      } catch (e) {
        setError('Failed to load result.');
      } finally {
        setLoading(false);
      }
    };

    fetchResult();
  }, []);

  if (loading) return <Layout><Spinner /></Layout>;

  if (error || !data) return (
    <Layout>
      <Alert type="error">{error || 'Result not found.'}</Alert>
      <Btn style={{ marginTop: 16 }} onClick={() => navigate('/')}>Back to History</Btn>
    </Layout>
  );

  const diseaseList = data.disease || [];
  const reportList = data.report || [];
  const abnormalCount = reportList.filter(r => !isNormal(r.Result, r['Ref. Range'])).length;
  const date = new Date(data.timestamp).toLocaleDateString();
  const time = new Date(data.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  const displayId = data.patient_id || patient_id || id;

  return (
    <Layout>
      <style>{`
        @media (max-width: 600px) {
          .history-actions { flex-wrap: wrap; gap: 8px !important; }
          .history-actions > * { flex: 1 1 auto; min-width: 80px; font-size: 13px; }
          .history-summary { grid-template-columns: repeat(3, 1fr) !important; }
          .history-info-grid { grid-template-columns: 1fr !important; }
          .history-table th:nth-child(3),
          .history-table td:nth-child(3) { display: none; }
        }
      `}</style>

      <PageHeader
        title="Urinalysis Report"
        subtitle={`${date} at ${time}`}
        actions={
          <div className="history-actions" style={{ display: 'flex', gap: 10 }}>
            <Btn variant="secondary" icon={ArrowLeft} onClick={() => navigate('/')}>Back</Btn>
            <Btn
              variant="outline"
              icon={Share2}
              onClick={() => navigate('/share', {
                state: {
                  result: {
                    ...data,
                    Report: reportList,
                    Disease: diseaseList,
                    AnalysisID: data?.id || 'N/A',
                    timestamp: data.timestamp
                  },
                  patientName: data.full_name,
                  patientId: displayId
                }
              })}
            >
              Share
            </Btn>
            <Btn icon={FlaskConical} onClick={() => navigate('/analysis')}>New</Btn>
          </div>
        }
      />

      {/* Summary cards */}
      <div
        className="history-summary"
        style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12, marginBottom: 20 }}
      >
        {[
          { label: 'Parameters', value: reportList.length, color: 'var(--blue)' },
          { label: 'Abnormal', value: abnormalCount, color: abnormalCount > 0 ? 'var(--red)' : 'var(--green)' },
          { label: 'Conditions', value: diseaseList.length, color: 'var(--amber)' },
        ].map(({ label, value, color }) => (
          <div
            key={label}
            style={{
              background: '#fff',
              borderRadius: 14,
              padding: '14px 16px',
              border: '1px solid var(--gray-200)',
              boxShadow: 'var(--shadow-sm)'
            }}
          >
            <p style={{ fontSize: 26, fontWeight: 700, color: 'var(--navy)', fontFamily: "'DM Serif Display', serif" }}>
              {value}
            </p>
            <p style={{ fontSize: 12, color: 'var(--gray-500)', marginTop: 3 }}>{label}</p>
            <div style={{ height: 3, background: color, borderRadius: 2, width: '40%', marginTop: 8, opacity: 0.6 }} />
          </div>
        ))}
      </div>

      {/* Patient / Examination info */}
      <div
        className="history-info-grid"
        style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}
      >
        <Section title="Patient Information">
          {[
            ['Patient ID', displayId],
            ['Name', data.full_name || 'N/A'],
            ['Kit Type', data.kit_type || 'N/A']
          ].map(([l, v]) => (
            <div
              key={l}
              style={{ display: 'flex', justifyContent: 'space-between', padding: '7px 0', borderBottom: '1px solid var(--gray-100)' }}
            >
              <span style={{ fontSize: 12, color: 'var(--gray-500)', fontWeight: 600 }}>{l}</span>
              <span style={{ fontSize: 13, color: 'var(--navy)', fontWeight: 600 }}>{v}</span>
            </div>
          ))}
        </Section>

        <Section title="Examination Info">
          {[
            ['Date', date],
            ['Time', time]
          ].map(([l, v]) => (
            <div
              key={l}
              style={{ display: 'flex', justifyContent: 'space-between', padding: '7px 0', borderBottom: '1px solid var(--gray-100)' }}
            >
              <span style={{ fontSize: 12, color: 'var(--gray-500)', fontWeight: 600 }}>{l}</span>
              <span style={{ fontSize: 13, color: 'var(--navy)', fontWeight: 600 }}>{v}</span>
            </div>
          ))}
        </Section>
      </div>

      {/* Diagnostic insights */}
      {diseaseList.length > 0 && (
        <Section title="🔬 Diagnostic Insights">
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {diseaseList.map((d, i) => (
              <div
                key={i}
                style={{
                  background: 'var(--blue-pale)',
                  borderRadius: 12,
                  padding: '14px 16px',
                  borderLeft: '4px solid var(--blue)'
                }}
              >
                <p style={{
                  fontSize: 11,
                  fontWeight: 700,
                  color: 'var(--blue)',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                  marginBottom: 4
                }}>
                  Possible Condition
                </p>
                <p style={{ fontSize: 15, fontWeight: 700, color: 'var(--navy)', marginBottom: 6 }}>
                  {d['Possible Disease']}
                </p>
                <p style={{ fontSize: 13, color: 'var(--gray-700)', lineHeight: 1.6 }}>
                  {d['Reason']}
                </p>
              </div>
            ))}
          </div>
        </Section>
      )}

      {/* Analysis table */}
      {reportList.length > 0 && (
        <Section title="📊 Analysis Summary">
          <div style={{
            overflowX: 'auto',
            borderRadius: 10,
            border: '1px solid var(--gray-200)',
            WebkitOverflowScrolling: 'touch'
          }}>
            <table
              className="history-table"
              style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, minWidth: 340 }}
            >
              <thead>
                <tr style={{ background: 'var(--navy)' }}>
                  {['Parameter', 'Result', 'Ref. Range', 'Status'].map(h => (
                    <th
                      key={h}
                      style={{
                        padding: '11px 14px',
                        textAlign: 'left',
                        color: '#fff',
                        fontWeight: 600,
                        fontSize: 12,
                        whiteSpace: 'nowrap'
                      }}
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {reportList.map((r, i) => {
                  const normal = isNormal(r.Result, r['Ref. Range']);
                  return (
                    <tr
                      key={i}
                      style={{
                        borderBottom: '1px solid var(--gray-100)',
                        background: i % 2 === 0 ? '#fff' : 'var(--gray-50)'
                      }}
                    >
                      <td style={{
                        padding: '10px 14px',
                        fontWeight: 600,
                        color: 'var(--navy)',
                        whiteSpace: 'nowrap'
                      }}>
                        {r['Analysis Type']}
                      </td>
                      <td style={{
                        padding: '10px 14px',
                        color: normal ? 'var(--gray-700)' : 'var(--red)',
                        fontWeight: normal ? 400 : 700,
                        fontFamily: "'JetBrains Mono', monospace",
                        fontSize: 12,
                        whiteSpace: 'nowrap'
                      }}>
                        {r.Result}
                      </td>
                      <td style={{
                        padding: '10px 14px',
                        color: 'var(--gray-500)',
                        fontFamily: "'JetBrains Mono', monospace",
                        fontSize: 12,
                        whiteSpace: 'nowrap'
                      }}>
                        {r['Ref. Range'] || '—'}
                      </td>
                      <td style={{ padding: '10px 14px' }}>
                        <Badge color={normal ? 'green' : 'red'}>
                          {normal ? 'Normal' : 'Abnormal'}
                        </Badge>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Section>
      )}
    </Layout>
  );
}